import React, { useState, useEffect } from 'react';
import {
  Box, 
  Container,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Button,
  Text,
  VStack,
  useToast,
  FormErrorMessage,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import AppHeader from '../components/AppHeader';

const CreatePassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  // Validation states
  const [passwordError, setPasswordError] = useState('');
  const [confirmError, setConfirmError] = useState('');

  // Redirect if not logged in or if password is already set
  useEffect(() => {
    const checkPasswordSet = async () => {
      if (!user) {
        navigate('/signup');
        return;
      }

      // Check if password_set is already true
      if (userProfile && userProfile.password_set) {
        navigate('/');
      }
    };

    checkPasswordSet();
  }, [user, userProfile, navigate]);

  const validateForm = () => {
    let isValid = true;
    setPasswordError('');
    setConfirmError('');
    
    if (password.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      isValid = false;
    }
    
    if (password !== confirmPassword) {
      setConfirmError('Passwords do not match');
      isValid = false;
    }
    
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Update the user's password
      const { error: updateError } = await supabase.auth.updateUser({
        password
      });
      
      if (updateError) throw updateError;
      
      // Update the password_set flag in public.users table
      const { error: dbError } = await supabase
        .from('users')
        .update({ password_set: true })
        .eq('id', user?.id);
      
      if (dbError) throw dbError;
      
      // Show success message
      toast({
        title: 'Password created',
        description: 'Your password has been set successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      
      // Redirect to home
      navigate('/');
      
    } catch (err: any) {
      setError(err.message || 'Failed to set password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box minH="100vh" bg="gray.900">
      <AppHeader
        playbackState={null}
        layoutMode="split"
        setLayoutMode={() => {}}
        accentColor="#FA2D48"
        pageTitle="Create Password"
        isCondensed={false}
      />
      
      <Container maxW="md" pt={20}>
        <VStack spacing={8} align="stretch">
          <Heading color="white" textAlign="center">Create Your Password</Heading>
          
          <Text color="gray.300" textAlign="center">
            Please set a password for your account to continue using all features.
          </Text>
          
          {error && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              {error}
            </Alert>
          )}
          
          <form onSubmit={handleSubmit}>
            <VStack spacing={4}>
              <FormControl isRequired isInvalid={!!passwordError}>
                <FormLabel color="white">Password</FormLabel>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter a secure password"
                  bg="gray.800"
                  color="white"
                  borderColor="gray.600"
                  _hover={{ borderColor: 'blue.300' }}
                  _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px blue.500' }}
                />
                {passwordError && <FormErrorMessage>{passwordError}</FormErrorMessage>}
              </FormControl>
              
              <FormControl isRequired isInvalid={!!confirmError}>
                <FormLabel color="white">Confirm Password</FormLabel>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  bg="gray.800"
                  color="white"
                  borderColor="gray.600"
                  _hover={{ borderColor: 'blue.300' }}
                  _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px blue.500' }}
                />
                {confirmError && <FormErrorMessage>{confirmError}</FormErrorMessage>}
              </FormControl>
              
              <Button
                type="submit"
                colorScheme="blue"
                width="100%"
                mt={4}
                isLoading={isLoading}
              >
                Create Password
              </Button>
            </VStack>
          </form>
        </VStack>
      </Container>
    </Box>
  );
};

export default CreatePassword; 